import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

export default function Panel() {
  const [neutrals, setNeutrals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/panel/public").then((r) => setNeutrals(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-sand" data-testid="panel-page">
      <header className="border-b hairline bg-sand">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-[13px] text-ink/70 inline-flex items-center hover:text-ink"><ArrowLeft className="h-4 w-4 mr-1" />Back to site</Link>
        </div>
      </header>

      <section className="bg-court text-sand">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-2"><div className="eyebrow-light">Our panel</div></div>
          <div className="lg:col-span-7">
            <h1 className="font-display text-[42px] lg:text-[64px] balanced" style={{ fontWeight: 400, lineHeight: 1.02 }}>
              Meet the mediators & arbitrators.
            </h1>
          </div>
          <div className="lg:col-span-3 text-sand/75 text-[14px]">
            Empaneled lawyers and trained professionals across India. Selected for qualification, language coverage and discipline.
          </div>
        </div>
      </section>

      <section className="bg-sand border-t hairline">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
          {loading ? (
            <div className="text-stone">Loading panel…</div>
          ) : neutrals.length === 0 ? (
            <div className="text-stone">No mediators or arbitrators to display.</div>
          ) : (
            <ul className="border-y hairline">
              {neutrals.map((n) => (
                <li key={n.id} className="grid grid-cols-12 gap-6 py-7 border-b border-stone/40 last:border-0" data-testid={`panel-row-${n.id}`}>
                  <div className="col-span-2 font-mono text-[11px] tracking-[0.12em] uppercase text-stone pt-1">
                    #{(n.cases_handled || 0).toString().padStart(3, "0")}
                  </div>
                  <div className="col-span-7">
                    <div className="font-display text-[22px] text-ink" style={{ fontWeight: 400 }}>{n.name}</div>
                    {(n.qualification || n.bar_id) && (
                      <div className="text-ink/70 text-[14px] mt-1">
                        {n.qualification || "—"}
                        {n.bar_id ? <> <span className="text-stone">·</span> Bar {n.bar_id}</> : null}
                      </div>
                    )}
                    <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-stone mt-2">
                      {(n.languages || []).join(" · ")} {n.districts?.length ? " · " : ""}{(n.districts || []).join(" · ")}
                    </div>
                  </div>
                  <div className="col-span-3 text-right">
                    <div className="eyebrow">Practice</div>
                    <div className="mt-1 text-ink text-[13px] capitalize">
                      {(n.specializations || []).join(", ") || "—"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="bg-ink text-sand">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2"><div className="eyebrow-light">Apply</div></div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-[28px] lg:text-[36px]" style={{ fontWeight: 400, lineHeight: 1.1 }}>
              Become an empaneled resolutionary.
            </h2>
            <p className="mt-3 text-sand/75 text-[15px] max-w-lg">Sign up, complete the ODR course, get reviewed by the empanelment committee. The work is online.</p>
          </div>
          <div className="lg:col-span-3 flex lg:justify-end">
            <Link to="/register" data-testid="panel-apply" className="inline-flex items-center gap-2 px-5 py-3 bg-clay text-sand hover:bg-[hsl(17_64%_42%)] text-sm" style={{ borderRadius: 3 }}>
              Apply to join <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
